import React from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ListingImageCoverBadge from './ListingImageCoverBadge';

/**
 * Celda de galería de fotos (UF o contenido de lote): portada, eliminar y ampliar.
 */
export default function ListingImageTile({
  image,
  canEdit = false,
  isDeleting = false,
  isSettingCover = false,
  showSetCover = false,
  showCoverBadge = true,
  onOpen,
  onDelete,
  onSetCover,
}) {
  const isBusy = isDeleting || isSettingCover;

  const handleImageClick = () => {
    if (isBusy) return;
    if (showSetCover) {
      onSetCover?.(image);
      return;
    }
    onOpen?.(image);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 1.5,
        overflow: 'hidden',
        border: image.is_cover
          ? '1px solid rgba(255,255,255,0.18)'
          : '1px solid rgba(255,255,255,0.08)',
        aspectRatio: '4/3',
        bgcolor: 'rgba(255,255,255,0.04)',
        opacity: isBusy ? 0.55 : 1,
        transition: 'opacity 0.2s ease, border-color 0.2s ease',
        '&:hover .delete-btn': { opacity: canEdit && !isBusy ? 1 : 0 },
        '&:hover .cover-hint': { opacity: showSetCover && !isBusy ? 1 : 0 },
      }}
    >
      <img
        src={image.url}
        alt={showSetCover ? 'Usar como portada' : 'foto del listado'}
        onClick={handleImageClick}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          cursor: isBusy ? 'default' : 'pointer',
        }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />

      {showCoverBadge && image.is_cover && <ListingImageCoverBadge />}

      {showSetCover && !isBusy && (
        <Box
          className="cover-hint"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            pb: 0.75,
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 0.15s ease',
            background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: '#FFFFFF',
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.02em',
              lineHeight: '14px',
              px: 0.75,
              py: 0.25,
              borderRadius: '4px',
              bgcolor: 'rgba(0,0,0,0.45)',
            }}
          >
            Usar como portada
          </Typography>
        </Box>
      )}

      {isBusy && (
        <Box sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.5)',
        }}
        >
          <CircularProgress size={16} sx={{ color: isDeleting ? '#f87171' : '#ffffff' }} />
        </Box>
      )}

      {canEdit && !isBusy && (
        <IconButton
          className="delete-btn"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(image);
          }}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            bgcolor: 'rgba(0,0,0,0.65)',
            opacity: 0,
            transition: 'opacity 0.15s ease',
            '&:hover': { bgcolor: 'rgba(211,33,33,0.85)' },
          }}
        >
          <DeleteIcon sx={{ fontSize: 14, color: '#ffffff' }} />
        </IconButton>
      )}
    </Box>
  );
}
